import { useState, useCallback, useMemo } from 'react';
import { validateEmail } from '../utils/emailValidation';

// バリデーションルールの型定義
export interface ValidationRule {
  validator: (value: unknown, formData?: Record<string, unknown>) => boolean;
  message: string;
}

// フィールドバリデーション設定の型定義
export interface FieldValidation {
  required?: boolean;
  rules?: ValidationRule[];
}

// バリデーション設定の型定義
export interface ValidationConfig {
  [fieldName: string]: FieldValidation;
}

// バリデーション結果の型定義
export interface ValidationErrors {
  [fieldName: string]: string | undefined;
}

/**
 * 共通フォームバリデーションフック
 *
 * 統一されたバリデーション処理を提供し、
 * エラーメッセージ管理とリアルタイムバリデーションを実装します。
 */
export const useFormValidation = (config: ValidationConfig) => {
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  // フィールドをタッチ済みとしてマーク
  const markFieldTouched = useCallback((fieldName: string) => {
    setTouchedFields(prev => new Set(prev).add(fieldName));
  }, []);

  // 単一フィールドのバリデーション
  const validateField = useCallback(
    (
      fieldName: string,
      value: unknown,
      formData?: Record<string, unknown>,
      markTouched = true
    ): string | undefined => {
      const fieldConfig = config[fieldName];
      if (!fieldConfig) return undefined;

      if (markTouched) {
        markFieldTouched(fieldName);
      }

      // 必須チェック
      if (fieldConfig.required) {
        if (value === undefined || value === null || value === '') {
          return 'この項目は必須です';
        }
        // 配列の場合は長さをチェック
        if (Array.isArray(value) && value.length === 0) {
          return 'この項目は必須です';
        }
      }

      // カスタムルールの実行
      if (fieldConfig.rules) {
        for (const rule of fieldConfig.rules) {
          if (!rule.validator(value, formData)) {
            return rule.message;
          }
        }
      }

      return undefined;
    },
    [config, markFieldTouched]
  );

  // 全フィールドのバリデーション
  const validateAllFields = useCallback(
    (formData: Record<string, unknown>): ValidationErrors => {
      const newErrors: ValidationErrors = {};

      Object.keys(config).forEach(fieldName => {
        const error = validateField(fieldName, formData[fieldName], formData, false);
        if (error) {
          newErrors[fieldName] = error;
        }
      });

      // すべてのフィールドをタッチ済みとしてマーク
      setTouchedFields(new Set(Object.keys(config)));
      setErrors(newErrors);

      return newErrors;
    },
    [config, validateField]
  );

  // リアルタイムフィールドバリデーション（タッチ済みフィールドのみ）
  const validateFieldIfTouched = useCallback(
    (fieldName: string, value: unknown, formData?: Record<string, unknown>) => {
      if (touchedFields.has(fieldName)) {
        const error = validateField(fieldName, value, formData, false);
        setErrors(prev => ({
          ...prev,
          [fieldName]: error,
        }));
        return error;
      }
      return undefined;
    },
    [validateField, touchedFields]
  );

  // フォームが有効かどうか
  const isFormValid = useMemo(() => {
    return Object.values(errors).every(error => !error);
  }, [errors]);

  // エラーがあるフィールド数
  const errorCount = useMemo(() => {
    return Object.values(errors).filter(error => !!error).length;
  }, [errors]);

  // エラーメッセージをクリア
  const clearErrors = useCallback(() => {
    setErrors({});
    setTouchedFields(new Set());
  }, []);

  // 特定フィールドのエラーをクリア
  const clearFieldError = useCallback((fieldName: string) => {
    setErrors(prev => ({
      ...prev,
      [fieldName]: undefined,
    }));
  }, []);

  return {
    errors,
    touchedFields,
    isFormValid,
    errorCount,
    validateField,
    validateAllFields,
    validateFieldIfTouched,
    markFieldTouched,
    clearErrors,
    clearFieldError,
  };
};

// 共通バリデーションルール
export const commonValidationRules = {
  // メールアドレス（統一バリデーション使用）
  email: {
    validator: (value: unknown) => {
      if (!value) return true; // 空の場合はOK（必須チェックは別途）
      return validateEmail(String(value)).isValid;
    },
    message: '有効なメールアドレスを入力してください',
  },

  // 必須メールアドレス
  requiredEmail: {
    validator: (value: unknown) => {
      return validateEmail(String(value), { required: true }).isValid;
    },
    message: '有効なメールアドレスを入力してください',
  },

  // 電話番号
  phone: {
    validator: (value: unknown) => {
      if (!value) return true;
      if (typeof value !== 'string') return false;
      const phoneRegex = /^[\d\-()\\s]+$/;
      return phoneRegex.test(value);
    },
    message: '有効な電話番号を入力してください',
  },

  // 最小文字数
  minLength: (min: number) => ({
    validator: (value: unknown) => {
      if (!value) return true;
      if (typeof value !== 'string') return false;
      return value.length >= min;
    },
    message: `${min}文字以上入力してください`,
  }),

  // 最大文字数
  maxLength: (max: number) => ({
    validator: (value: unknown) => {
      if (!value) return true;
      if (typeof value !== 'string') return false;
      return value.length <= max;
    },
    message: `${max}文字以下で入力してください`,
  }),

  // 数値
  numeric: {
    validator: (value: unknown) => {
      if (!value) return true;
      if (typeof value !== 'string') return false;
      return !isNaN(Number(value));
    },
    message: '数値を入力してください',
  },

  // 正の数値
  positiveNumber: {
    validator: (value: unknown) => {
      if (!value) return true;
      if (typeof value !== 'string') return false;
      const num = Number(value);
      return !isNaN(num) && num > 0;
    },
    message: '正の数値を入力してください',
  },
};

export default useFormValidation;
